import { ColDef, GridOptions } from "ag-grid-community";
import { Box } from "@mui/material";
import { DataTableV2, defaultColDef } from "@platform/design-system";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { STEWARDSHIP_DISCOVERABLE_SORT } from "@platform/feature-flags";
import { useMemo } from "react";

// Quartz theme + per-cell borders, mirroring ScenarioDetailsTable. The
// min-height override stops AG Grid from reserving an empty page-sized gap when
// there are fewer rows than a full page.
const TABLE_SX = {
  width: "100%",
  "& .ag-cell": {
    borderRight: "1px solid var(--ag-border-color, #dde2eb)",
  },
  "& .ag-center-cols-viewport": {
    minHeight: "unset !important",
  },
} as const;

// Columns size responsively via `flex` (no fixed widths, no horizontal scroll)
// and are sortable. Filtering, resizing and reordering stay off — these are
// read-only report grids. `unSortIcon` (the persistent neutral sort indicator)
// is applied per-render behind the STEWARDSHIP_DISCOVERABLE_SORT flag.
const BASE_GRID_OPTIONS: GridOptions = {
  pagination: false,
  defaultColDef: {
    sortable: true,
    filter: false,
    resizable: false,
    suppressMovable: true,
    flex: 1,
  },
};

// Merge the design-system defaults under each column so cells pick up the
// shared header wrapping while letting callers override per column.
//
// The design-system defaultColDef sets `cellClass: "centered-cell"` and a
// `cellStyle`. Both are cleared here: that cellClass overrides the class AG
// Grid's `type: "rightAligned"` applies, which would leave numeric columns
// left-aligned. Clearing them lets each column's own type / styles win, while a
// column can still set its own cellClass/cellStyle/wrapText to opt back in.
const applyDefaultColDef = <TRow,>(col: ColDef<TRow>): ColDef<TRow> => ({
  // defaultColDef is row-agnostic (ColDef<any>); the row-specific typing comes
  // from `col`, so cast the default to the column's row type before merging.
  ...(defaultColDef as ColDef<TRow>),
  cellClass: undefined,
  cellStyle: undefined,
  ...col,
  wrapText: col.wrapText ?? false,
});

type StewardshipTableProps<TRow> = {
  rowData: TRow[];
  columnDefs: ColDef<TRow>[];
};

function StewardshipTable<TRow>({
  rowData,
  columnDefs,
}: StewardshipTableProps<TRow>): React.JSX.Element {
  const showSortIndicator = useFeatureIsOn(STEWARDSHIP_DISCOVERABLE_SORT);

  const columns = useMemo(
    () => columnDefs.map(applyDefaultColDef),
    [columnDefs],
  );

  const gridOptions = useMemo<GridOptions>(
    () => ({
      ...BASE_GRID_OPTIONS,
      defaultColDef: {
        ...BASE_GRID_OPTIONS.defaultColDef,
        unSortIcon: showSortIndicator,
      },
    }),
    [showSortIndicator],
  );

  return (
    <Box className="ag-theme-quartz" sx={TABLE_SX}>
      <DataTableV2
        rowData={rowData}
        columnDefs={columns}
        gridOptions={gridOptions}
      />
    </Box>
  );
}

export { StewardshipTable };
